/**
 * Tunable constants + message types shared between the main-thread tanpura
 * synth (`tanpura.ts`) and its AudioWorklet string model (`tanpura-string-processor.ts`).
 * Kept in one place so the whole sound can be re-tuned without hunting through
 * two files that run in two different JS realms.
 *
 * v2 re-tune (this pass): measured FFTs of two real tanpura recordings
 * (`scripts/data/tanpura-spectral-targets.md`) show the real instrument is
 * NOT fundamental-dominant with a gentle rolloff — a broad "jivari formant"
 * makes mid harmonics (~h4-h10, peak ~5x f0) the LOUDEST part of the
 * spectrum, with the fundamental 10-20 dB below that peak and real energy
 * surviving out to ~h20+. The v1 synth (plain Karplus-Strong + a single swept
 * resonator riding on the KS output) measured the opposite: h1 at the peak,
 * −60dB+ by h8 (see git history / the v1 BEFORE capture in the FFT-check
 * report). This pass replaces the single swept resonator with a static BANK
 * of resonators — one per harmonic, each independently excited (on the same
 * pluck) by a single deterministic impulse, then ringing on its own at its
 * own frequency and decay rate — a well-known DSP technique (modal/resonant
 * filter-bank synthesis: a parallel bank of 2-pole resonators fed a common
 * impulse, each mode ringing at k*f0) that gives direct, per-harmonic
 * control over the spectral envelope instead of relying on the KS delay
 * line's own (fundamental-dominant, fast-rolling-off) spectrum. Every
 * constant below is verified against real measurements via
 * `scripts/tanpura-fft-check.mjs`, not by ear (nobody on this task can hear
 * the output).
 *
 * Sources consulted (fetched during the original v1 pass; still the acoustic
 * grounding for the string/pluck-cycle side of the model):
 *  - https://en.wikipedia.org/wiki/Tanpura — 4-string tuning "Pa(or Ma)-Sa-Sa-Sa̠"
 *    (5-8-8-1: fifth, tonic, tonic, tonic an octave down); the curved "jivari"
 *    bridge; grazing string/bridge contact that migrates over ~3-10 seconds as
 *    the string's energy decays.
 *  - https://en.wikipedia.org/wiki/Jivari — the curved, flat-parabolic bridge;
 *    the grazing contact point "gradually shifts up the sloping surface" as
 *    amplitude decreases, producing "a cascading row of harmonics ... a rainbow
 *    of sound"; octaves/fifths/major-thirds/minor-sevenths are the harmonics
 *    singled out as characteristically prominent.
 *  - https://en.wikipedia.org/wiki/Karplus%E2%80%93Strong_string_synthesis —
 *    delay length D = Fs/F0; a short (originally white-noise) excitation burst;
 *    a feedback loop whose filter gain must be < 1 at all frequencies for
 *    stability; the original filter is a two-tap sample average, which is what
 *    gives the classic "warm, damped string" timbre (highs die faster than the
 *    fundamental) — used here instead of a flat multiplicative decay.
 *  - Jaffe, D. & Smith, J.O. (1983), "Extended Karplus-Strong Algorithm" —
 *    decay time can be detached from the raw per-loop filter response by
 *    solving for a loop gain that hits a target T60; that's the approach taken
 *    below (`KS_DECAY_T60_S` drives `loopGain`, not a hard-coded multiplier;
 *    the same technique now also derives each formant mode's pole radius from
 *    `FORMANT_MODE_DECAY_S`).
 *  - `scripts/data/tanpura-spectral-targets.md` — the FFT-measured targets
 *    (strongest partial h4-h10, jivari ratio >= ~3, fundamental 10-20dB down,
 *    energy to ~h20+) this file's formant constants are tuned against.
 */

/** Default tempo when the caller doesn't pass one. `tanpura.ts` plucks one
 * string every `60 / DEFAULT_TEMPO_BPM` seconds; the spectral-targets doc
 * calls for a slow, meditative ~1.3-1.8s between plucks by default (real
 * recordings ranged 0.9s-2.5s), so 40 BPM -> 1.5s sits in the middle of that
 * range. Still tempo-linked: callers can override via `TanpuraOptions.tempoBpm`. */
export const DEFAULT_TEMPO_BPM = 40;

// --- Karplus-Strong plucked-string model -----------------------------------

/** Target time (seconds) for the plain Karplus-Strong core to decay ~60 dB.
 * This layer is now a MINOR voice (see `KS_DIRECT_MIX` below) — it supplies
 * the pluck attack transient and a touch of low-end body, while the formant
 * resonator bank below carries the spectrum. Long + overlapping plucks
 * (cycle period ~1.5s at the default tempo) is what makes the drone read as
 * continuous rather than a metronomic sequence of separate notes; real
 * tanpura strings ring for several seconds, and this is on the long side of
 * that so 3-4 strings stay audibly overlapping. */
export const KS_DECAY_T60_S = 7;

/** Length (ms) of the noise burst that excites a string on each pluck. Roughly
 * one fundamental period at typical Sa/Pa pitches, per the original Karplus-Strong
 * excitation (a short noise burst the length of the delay line). Windowed
 * (Hann) so it fades in/out rather than starting/stopping abruptly — that's
 * what keeps the pluck click-free. */
export const KS_EXCITATION_MS = 6;

/** Linear amplitude of the excitation burst before the two-tap averaging filter
 * and T60-derived loop gain take over. Lower = softer pluck attack. */
export const PLUCK_GAIN = 0.85;

/** Detune (cents) applied to the second of the two unison Sa strings. Real
 * tanpura strings are tuned by ear/hand one at a time and are never perfectly
 * identical; a few cents of mistuning between two strings at the same nominal
 * pitch is what produces the slow, "breathing" beating that makes a drone feel
 * alive rather than static. */
export const DETUNE_CENTS = 4;

/** How much of the raw Karplus-Strong core survives into the final per-string
 * output (1.0 = full strength, matching the un-attenuated fundamental-heavy
 * v1 sound). The KS core alone is fundamental-dominant (see the BEFORE
 * capture in the FFT-check report) — this is the "fundamental attenuation"
 * knob: it's what keeps h1 well below the formant peak once the two layers
 * are summed in `process()`. Lower = weaker, more distant fundamental/attack;
 * too low and the pluck loses its transient "pick" entirely. */
export const KS_DIRECT_MIX = 0.09;

// --- Jivari formant (bank of resonators = the mid-harmonic-dominant "buzz") -
//
// The real jivari bridge doesn't add a single tone — it reshapes the whole
// spectral envelope, boosting a broad cluster of mid harmonics far above the
// fundamental (see tanpura-spectral-targets.md). Modeled here as a bank of
// static 2-pole resonators, one per harmonic index k=1..FORMANT_TOP_HARMONIC,
// each tuned to k * f0 and excited by a single deterministic impulse on every
// pluck (the KS delay line gets its own separate noise-burst excitation — see
// KS_EXCITATION_MS below — so the two layers share one physical pluck event
// without sharing randomness). A true impulse has a flat, deterministic
// frequency response, so each mode's resulting amplitude is exactly
// proportional to its own gain below — no run-to-run variance from a short
// random burst's own uneven spectrum to fight when tuning against measured
// targets. A parallel resonant filter bank driven by an impulse is the
// standard way to give independent, per-partial control over both amplitude
// and decay instead of inheriting whatever spectrum the KS delay line
// happens to produce.

/** Which harmonic (as a multiple of f0) the formant bump is centred on.
 * tanpura-spectral-targets.md: real recordings' strongest partial was h4-h6;
 * centring the bump at 5.5 puts h5 and h6 essentially tied for loudest. */
export const FORMANT_CENTER_HARMONIC = 5.5;

/** Width (standard deviation, in harmonic-index units) of the Gaussian bump
 * centred on `FORMANT_CENTER_HARMONIC`. Smaller = narrower/more nasal bump
 * (sharper falloff into h1-h3 and h11+); larger = broader, smoother
 * shoulders. Tuned so the bump's "shoulders" land around h4/h10 (the
 * measured strong-cluster edges) while h1-h3 sit comfortably below it. */
export const FORMANT_WIDTH_HARMONICS = 1.8;

/** Topmost harmonic modeled by the resonator bank. tanpura-spectral-targets.md
 * calls for "real energy present through ~h20+"; modeling a few harmonics
 * past that keeps h20 itself from sitting right at the bank's own edge. */
export const FORMANT_TOP_HARMONIC = 24;

/** A broad, slowly-tapering floor added on top of the Gaussian bump so upper
 * partials (h11+, past the bump's shoulder) don't die to nothing — this is
 * the sustained "shimmer"/"cascading row of harmonics" the jivari source
 * describes, extending energy out past the main formant instead of a clean
 * single bump. Value is relative to the bump's own peak (1.0). */
export const FORMANT_SHIMMER_FLOOR = 0.12;

/** e-folding harmonic count for the shimmer floor's own gentle taper (it
 * still fades with harmonic index, just far more slowly than the Gaussian
 * bump) — larger = flatter/further-reaching shimmer. */
export const FORMANT_SHIMMER_DECAY_HARMONICS = 14;

/** Overall drive of the resonator bank relative to the excitation burst's own
 * amplitude. This is the master "how loud is the formant vs. everything
 * else" knob — raise/lower this (not the KS layer) to change the drone's
 * overall buzz intensity while keeping the KS/formant balance (and hence the
 * fundamental-below-peak margin) intact. */
export const FORMANT_EXCITATION_GAIN = 5.2;

/** Target T60 (seconds) for each formant mode's own decay. All modes share
 * one decay time, so the RELATIVE balance between harmonics (and hence the
 * jivari ratio / strongest-partial position) stays constant for as long as
 * the string rings — only the overall level fades. Comparable to
 * `KS_DECAY_T60_S` so the two layers fade together rather than one
 * outlasting the other and shifting the timbre late in the note. */
export const FORMANT_MODE_DECAY_S = 6;

// --- Master bus: warmth + space ---------------------------------------------

/** Gentle master lowpass to round off the top end of the combined KS +
 * formant signal so the buzz reads as "shimmering" rather than
 * "harsh/aliased". Raised from the v1 value so it sits safely above
 * `FORMANT_TOP_HARMONIC` at typical Sa/Pa pitches instead of cutting the
 * shimmer's own top end back off right after the formant bank creates it. */
export const MASTER_LOWPASS_HZ = 9000;

/** Synthesized reverb impulse response: length and exponential falloff
 * exponent of the decaying-noise IR (higher exponent = faster falloff, drier
 * tail). This is a small, cheap stand-in for the room/body resonance a real
 * tanpura is normally heard in — not modeling the instrument itself. */
export const REVERB_DURATION_S = 2.2;
export const REVERB_DECAY_EXPONENT = 3.2;
/** Wet/dry mix (0 = fully dry, 1 = fully wet) applied on the master bus. */
export const REVERB_MIX = 0.22;

// --- Worklet message protocol ------------------------------------------------

/** Frequencies for the 4 cycle slots, in pluck order: [first (Pa/Ma), Sa, Sa
 * (detuned), Sa̠ (lower octave)] — see the Tanpura source above for the 5-8-8-1
 * tuning and pluck order this mirrors. */
export interface TanpuraProcessorOptions {
  frequencies: number[];
}

export interface TanpuraPluckMessage {
  type: 'pluck';
  index: number;
}
